/**
 * Отказ сервера так, как его должен прочитать человек.
 *
 * Обычный `Error` из функции Convex до экрана не доезжает: в ПРОДЕ сообщение
 * вырезается и остаётся «Server Error». Поэтому отказы, которые человеку надо
 * прочитать, бросаются `ConvexError` со структурой, а здесь читается её
 * полезная нагрузка. Найдено 3 сентября 2026 на гейте: сквозная проба
 * получила «Server Error» там, где `npx convex run` печатал текст целиком.
 */
import { ConvexError } from "convex/values";

/** Полезная нагрузка как объект, или `null` — не `ConvexError`. */
function payload(e: unknown): Record<string, unknown> | null {
  if (!(e instanceof ConvexError)) return null;
  const data: unknown = e.data;
  return typeof data === "object" && data !== null ? (data as Record<string, unknown>) : null;
}

/** Текст причины: из нагрузки `ConvexError`, иначе из самой ошибки. */
export function reasonOf(e: unknown): string {
  if (e instanceof ConvexError && typeof e.data === "string") return e.data;
  const message = payload(e)?.message;
  if (typeof message === "string") return message;
  return e instanceof Error ? e.message : String(e);
}

/**
 * Отказ «ученик не найден» — это вход без ключа, а не сбой.
 *
 * Сверяется `kind` из нагрузки, а не текст: сравнение со строкой развалилось
 * бы от первой правки формулировки, а в проде сверять было бы не с чем.
 * Бросает `resolveLearner` (convex/lib/access.ts).
 */
export function isNoLearner(e: unknown): boolean {
  return payload(e)?.kind === "no-learner";
}

/**
 * Замечания разметки из отказа `tutor.saveTheory`, иначе `null`.
 *
 * Список, а не строка: сервер возвращает ВСЕ замечания разом, потому что
 * править их по одному — это четыре круга сохранения вместо одного
 * (convex/lib/theoryText.ts).
 */
export function theoryErrorsOf(e: unknown): string[] | null {
  const data = payload(e);
  if (data === null || data.kind !== "theory-invalid") return null;
  const errors: unknown = data.errors;
  if (!Array.isArray(errors)) return null;
  return errors.filter((x): x is string => typeof x === "string");
}
